import { FC } from 'react';
import { StatsModal } from '../StatsModal';
import type { useMessagesGQL } from '../../../../hooks/graphql/dashboard/useMessagesGQL';
import { buildEmailColumns, type EmailThreadSelection } from './emailColumns';
import { useShowCrmStage } from '../../../../hooks/useShowCrmStage';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  dateRange: { from: Date | null; to: Date | null };
  onDateRangeChange?: (range: { from: Date | null; to: Date | null }) => void;
  timezone?: string;
  emailsGQL: ReturnType<typeof useMessagesGQL>;
  onOpenContact: (contactId: string) => void;
  onOpenThread: (thread: EmailThreadSelection) => void;
}

/**
 * Delivered drill-down (the "Emails" tile on the dashboard), in the SMS
 * drill-down's inbox layout: Remi conversations open as a thread, eAlert
 * and flow-template emails open as one email.
 */
export const EmailsStatsModal: FC<Props> = ({
  isOpen,
  onClose,
  dateRange,
  onDateRangeChange,
  timezone,
  emailsGQL,
  onOpenContact,
  onOpenThread,
}) => {
  const showCrmStage = useShowCrmStage();
  if (!isOpen) return null;
  return (
    <StatsModal
      isOpen={isOpen}
      onClose={() => {
        onClose();
        emailsGQL.setSearch?.(undefined);
      }}
      title="Emails"
      dateRange={dateRange}
      onDateRangeChange={onDateRangeChange}
      onSearch={(q) => emailsGQL.setSearch?.(q || undefined)}
      searchPlaceholder="Search by name or subject..."
      currentPage={emailsGQL.currentPage}
      pageSize={emailsGQL.pageSize}
      onNextPage={emailsGQL.nextPage}
      onPrevPage={emailsGQL.prevPage}
      onPageChange={emailsGQL.goToPage}
      onPageSizeChange={emailsGQL.changePageSize}
      totalCount={emailsGQL.total}
      isLoading={emailsGQL.isLoading}
      // Whole-row click opens the contact, like the SMS drill-down; the row's
      // "Open thread" / "Preview email" action opens the email itself.
      onRowClick={(item) => {
        const id = item.contactId as string | undefined;
        if (id) onOpenContact(id);
      }}
      columns={buildEmailColumns({ timezone, onOpenContact, onOpenThread, showCrmStage })}
      data={emailsGQL.items || []}
    />
  );
};

export default EmailsStatsModal;
